<template>
  <page :name="$t('Listino prezzi')" :state="[catalogStore, ccpState]" sticky v-model:filter="q">
    <template #toolbar-post>
      <pf-toolbar-item v-if="!login.currency_locked">
        <currency-toggle id="currency-dropdown" :prefix="$t('Valuta')" />
      </pf-toolbar-item>

      <pf-toolbar-item>
        <pf-dropdown id="invoicing-dropdown" :text="$t('Fatturazione') + ': ' + $t(selectDurata)">
          <pf-dropdown-item @click.prevent="selectDurata = 'Annuale'">
            {{ $t("Annuale") }}
          </pf-dropdown-item>
          <pf-dropdown-item @click.prevent="selectDurata = 'Mensile'">
            {{ $t("Mensile") }}
          </pf-dropdown-item>
        </pf-dropdown>
      </pf-toolbar-item>

      <pf-toolbar-item v-if="login.is_reseller">
        <pf-button @click="showCcpForm = true">
          <template #icon>
            <edit-icon />
          </template>
          {{ $t("Modifica ricarico") }}
        </pf-button>
      </pf-toolbar-item>
    </template>

    <template v-for="category of PRODUCTCATEGORY">
      <pf-page-section no-body-wrapper
        v-if="catalog[category] && catalog[category].length > 0"
        :key="category"
        style="margin-top: 1em"
      >
        <pf-flex justify-content="space-between">
          <pf-flex-item align-self="flex-start">
            <pf-title h="2">
              {{ category }}
            </pf-title>
          </pf-flex-item>

          <pf-flex-item align-self="flex-end">
            <pf-button
              v-if="login.is_reseller && ricaricoByCategoria(category) != undefined"
              variant="control"
              small
              @click="showCcpForm = true"
            >
              <template #icon>
                <edit-icon />
              </template>
              {{ $t("Ricarico personalizzato: +{0}%", [ricaricoByCategoria(category)]) }}
            </pf-button>
          </pf-flex-item>
        </pf-flex>

        <p style="max-width: 80ch">{{ categoryDescription[category] }}</p>

        <catalog-product-list
          v-if="renderCategories.includes(category)"
          :items="catalog[category] ?? []"
          :currency="login.currency"
          :hide-plus-vat="login.iva < 0.01"
        />

        <pf-skeleton
          :ref="(c: SkeletonInstance | null) => c && skeletons.set(c.$el)"
          v-else
          :data-category="category"
          width="100%"
          :height="`${130 * (catalog[category]?.length || 1)}px`"
        />
      </pf-page-section>
    </template>

    <category-custom-prices-form
      v-if="login.is_reseller"
      v-model:open="showCcpForm"
      :category-custom-prices="ccp"
      @submit="ccpSubmit"
    />
  </page>
</template>

<script lang="ts" setup>
import { $t as _t } from "@/i18n";
import EditIcon from "@vue-patternfly/icons/edit-alt-icon";
import { asyncComputedList } from "@common/asyncComputed";
import { any } from "@common/utils";
import CatalogProductList from "../components/catalog-product-list.vue";
import CurrencyToggle from "../components/currency-toggle.vue";
import CategoryCustomPricesForm from "../components/category-custom-prices-form.vue";
import { useLoginStore } from "@/store/login";
import { useCatalogStore } from "@/store/catalog";
import { CategoryCustomPricesResource, type CatalogProduct } from "@/resources";
import { computed, ref, onMounted, type Ref } from "vue";
import { PRODUCTCATEGORY } from "@common/consts.ts";
import { useIntersectionObserver, useTemplateRefsList } from "@vueuse/core";
import type PfSkeleton from "@vue-patternfly/core/components/Skeleton.vue";
import { type PRODOTTIANAGRAFICA_DURATA } from "../common/consts";

type SkeletonInstance = InstanceType<typeof PfSkeleton>;

export type NormalizedCatalogProduct = CatalogProduct & {
  prezzo: number;
  prezzo_scontato: number;
  prezzo_ivato: number;
  prezzo_customer?: number | null;
  qt?: number;
};

defineOptions({
  name: "CatalogPage",
});

const categoryDescription: Record<(typeof PRODUCTCATEGORY)[number], string | null> = {
  "Internet Domain": _t(
    "Con oltre 200 estensioni TLD, gestione DNS e redirect, la registrazione del dominio offre un servizio affidabile e flessibile, assicurando una presenza online sempre raggiungibile, con fatturazione annuale.",
  ),
  "Internet Domain — Add On": _t(
    "Offriamo servizi esclusivi per la gestione e la protezione avanzata dei domini, dalla registrazione premium completa alla gestione avanzata del solo DNS.",
  ),
  "Cloud Hosting": _t(
    "Offriamo servizi esclusivi per la gestione e la protezione avanzata dei domini, dalla registrazione premium completa alla gestione avanzata del solo DNS.",
  ),
  "Cloud Hosting — Windows": _t(
    "Garantisce velocità, sicurezza e stabilità. Con Plesk, protezione avanzata e backup automatici, offre risorse scalabili e affidabilità, adattandosi a ogni esigenza, dalle basi solide di Oblivion alla massima potenza di Eclipse.",
  ),
  "Cloud Hosting — Mail": _t(
    "Garantisce affidabilità, sicurezza e prestazioni elevate. Con storage All-flash e backup in location separata, offre caselle illimitate e supporto proattivo, con soluzioni scalabili da 10 GB per esigenze base fino a 120 GB per massima capacità.",
  ),
  "Cloud Reseller": _t(
    "Garantisce flessibilità, autonomia e ottimizzazione dei costi. Con infrastruttura avanzata e supporto operativo, offre gestione reseller scalabile e trasferimento clienti. Disponibile in versione self-managed per il massimo controllo o managed per assistenza completa.",
  ),
  "Cloud Server": _t(
    "Garantisce prestazioni elevate e risorse scalabili in tempo reale. Flessibile e adattabile a ogni esigenza, offre un’infrastruttura potente e affidabile per ottimizzare costi e performance senza compromessi.",
  ),
  "Cloud Disaster Recovery": _t(
    "Soluzioni di Disaster Recovery progettate per garantire continuità operativa, protezione dei dati e ripristino rapido dei servizi in caso di guasti, incidenti, errori umani o eventi critici. Grazie a infrastrutture ridondate, backup in location separata e procedure di ripartenza controllate, il servizio permette di ridurre i tempi di fermo e proteggere applicazioni, server e dati aziendali.",
  ),
  "Host Server": _t(
    "Infrastruttura dedicata e sicura per applicazioni aziendali e servizi business-critical. Offre prestazioni elevate, risorse esclusive e scalabilità, con storage all-flash distribuito e protezione avanzata.",
  ),
  "Cloud Server GPU": _t(
    "Accelera il training e l’inferenza dei tuoi modelli di intelligenza artificiale con i nostri Cloud Server GPU: potenti, scalabili e pronti all’uso. Basati su schede grafiche NVIDIA (H100, H200, A100, L40S, RTX A6000, L4) e AMD MI300X, offrono prestazioni elevate per deep learning, computer vision, big data processing e altri workload complessi. Perfetti anche per utilizzi brevi come test o sviluppo.",
  ),
  "Cloud Server — Stack": _t(
    "Soluzioni avanzate per ambienti Linux e Windows. Con configurazioni ottimizzate e sicurezza integrata, offrono massima personalizzazione e gestione efficiente per qualsiasi progetto.",
  ),
  "Cloud & Host server — Add On": _t(
    "Strumenti avanzati software e hardware per potenziare sicurezza e performance. Dalle licenze Microsoft, alla protezione Imunify360 e risorse scalabili, per garantire massima flessibilità e ottimizzazione per ogni esigenza.",
  ),
  Storage: _t(
    "Garantisce storage cloud flessibile e sicuro. Con soluzioni scalabili, ridondanti e ad alte prestazioni, offre gestione avanzata per archiviazione dati, server e email, adattandosi a qualsiasi esigenza operativa.",
  ),
  Backup: _t(
    "Garantisce backup sicuro e gestito con storage All-flash da 100 GB. Con soluzioni da Acronis a IBM Spectrum Protect, offre protezione avanzata e recupero rapido, adattandosi a qualsiasi esigenza, dalla gestione essenziale alle soluzioni enterprise.",
  ),
  "SSL Certificates": _t(
    "Garantisce protezione avanzata per siti e aziende. Con opzioni da SSL base a certificati Wildcard ed EV, offre sicurezza scalabile e copertura assicurativa fino a 250.000€, adattandosi a ogni esigenza, dalla protezione essenziale alla massima affidabilità.",
  ),
  "Posta elettronica certificata": _t(
    "Garantisce una gestione PEC sicura e professionale con caselle certificate su dominio personalizzato. Con opzioni per archiviazione, spazio aggiuntivo e notifiche SMS, offre soluzioni scalabili per aziende e professionisti.",
  ),
  "Internet Domain — Other TLD": _t(
    "Con oltre 200 estensioni TLD, gestione DNS e redirect, la registrazione del dominio offre un servizio affidabile e flessibile, assicurando una presenza online sempre raggiungibile, con fatturazione annuale. In caso di domini classificati come premium, il costo di registrazione potrebbe essere superiore rispetto alle tariffe standard. In questi casi, la registrazione verrà preventivamente quotata e attivata solo previa conferma.",
  ),
  Development: null,
  Datacenter: null,
  Commissions: null,
  Comarketing: null,
  Other: null,
};

const login = useLoginStore();
const catalogStore = useCatalogStore();

const renderCategories: Ref<string[]> = ref([]);
const skeletons = useTemplateRefsList<HTMLDivElement>();
useIntersectionObserver(skeletons, ([entry]) => {
  if (!entry?.isIntersecting || !(entry.target instanceof HTMLElement)) {
    return;
  }
  const category = entry.target.dataset.category;
  if (category && !renderCategories.value.includes(category)) {
    renderCategories.value.push(category);
  }
});

onMounted(catalogStore.init);

const q = ref("");
const selectDurata = ref<(typeof PRODOTTIANAGRAFICA_DURATA)[number]>("Annuale");
const showCcpForm = ref(false);

const { state: ccpState, result: ccp } = asyncComputedList(() => {
  if (login.is_reseller) {
    return new CategoryCustomPricesResource().get();
  }
  return [];
});

const catalog = computed(() => {
  const categorized: Partial<Record<(typeof PRODUCTCATEGORY)[number], NormalizedCatalogProduct[]>> =
    {};

  const filter = any(q.value);
  for (const item of catalogStore.products) {
    if (filter(item) && filtroDurata(item) && item.codice !== "TONEETTRANS") {
      if (!Object.prototype.hasOwnProperty.call(categorized, item.categoria)) {
        categorized[item.categoria] = [];
      }

      categorized[item.categoria]?.push({
        ...item,
        prezzo: login.currency == "EUR" ? item.prezzo_eur : item.prezzo_chf,
        prezzo_scontato:
          login.currency == "EUR" ? item.prezzo_scontato_eur : item.prezzo_scontato_chf,
        prezzo_ivato: login.currency == "EUR" ? item.prezzo_ivato_eur : item.prezzo_ivato_chf,
        prezzo_customer:
          login.currency == "EUR" ? item.prezzo_clienti_eur : item.prezzo_clienti_chf,
      });
    }
  }
  return categorized;
});

function filtroDurata(prodotto: CatalogProduct) {
  return prodotto.durata == "Una tantum" || prodotto.durata == selectDurata.value;
}

function ricaricoByCategoria(category: string) {
  if (login.is_reseller) {
    return category
      ? ccp.value.filter((v) => v.category == category.replace(/(^| — )\d+\.\s*/g, "$1"))[0]?.markup
      : 0;
  }
  return undefined;
}

function ccpSubmit() {
  showCcpForm.value = false;
  ccpState.update();
  catalogStore.load();
}
</script>
